import { CCard, CCardBody, CCardHeader, CCol, CRow } from "@coreui/react";
import { FieldError, UseFormReturn } from "react-hook-form"; // Added UseFormReturn
import React, { useMemo } from "react"; // Removed useEffect, useForm
import { SettingDefinition, UserSettings } from "../../../types/userTypes";

import BooleanField from "./fields/BooleanField";
import NumberField from "./fields/NumberField";
import SelectField from "./fields/SelectField";
import TextAreaField from "./fields/TextAreaField";
import TextField from "./fields/TextField";

interface DynamicSettingsFormProps {
  definitions: SettingDefinition[];
  formMethods: UseFormReturn<UserSettings>; // Pass all form methods
}

const DynamicSettingsForm: React.FC<DynamicSettingsFormProps> = ({
  definitions,
  formMethods,
}) => {
  const {
    register,
    // handleSubmit, // No longer needed here
    setValue,
    watch,
    formState: { errors },
    // reset, // No longer needed here
  } = formMethods;

  // Group settings by category
  const settingsByCategory = useMemo(() => {
    const grouped: Record<string, SettingDefinition[]> = {};
    definitions
      .sort((a, b) => a.order - b.order)
      .forEach((def) => {
        if (!grouped[def.category]) {
          grouped[def.category] = [];
        }
        grouped[def.category].push(def);
      });
    return grouped;
  }, [definitions]);

  const renderField = (definition: SettingDefinition) => {
    const fieldName = definition.key;
    const fieldValue = watch(fieldName) ?? definition.default;
    const fieldError = errors[fieldName] as FieldError | undefined;

    const sanitizedKey = fieldName.replace(/_/g, "-");

    const commonProps = {
      name: fieldName,
      label: definition.label,
      description: definition.description,
      error: fieldError,
      required: definition.validation?.required || false,
    };

    // Register the field for validation
    register(fieldName, {
      required: definition.validation?.required
        ? `${definition.label} is required`
        : false,
      ...(definition.type === "number" && { valueAsNumber: true }),
    });

    switch (definition.type) {
      case "string":
        return (
          <TextField
            {...commonProps}
            testId={`uprof-input-${sanitizedKey}`}
            value={fieldValue || ""}
            onChange={(value) => setValue(fieldName, value)}
            placeholder={definition.options?.placeholder}
          />
        );

      case "number":
        return (
          <NumberField
            {...commonProps}
            testId={`uprof-input-${sanitizedKey}`}
            value={fieldValue || 0}
            onChange={(value) => setValue(fieldName, value)}
            placeholder={definition.options?.placeholder}
            min={definition.options?.min}
            max={definition.options?.max}
            step={definition.options?.step}
          />
        );

      case "boolean":
        return (
          <BooleanField
            {...commonProps}
            testId={`uprof-chk-${sanitizedKey}`}
            value={fieldValue || false}
            onChange={(value) => setValue(fieldName, value)}
          />
        );

      case "select":
        const options = definition.options?.choices || [];
        return (
          <SelectField
            {...commonProps}
            testId={`uprof-select-${sanitizedKey}`}
            value={fieldValue || ""}
            onChange={(value) => setValue(fieldName, value)}
            options={options}
            placeholder={definition.options?.placeholder}
          />
        );

      case "textarea":
        return (
          <TextAreaField
            {...commonProps}
            testId={`uprof-textarea-${sanitizedKey}`}
            value={fieldValue || ""}
            onChange={(value) => setValue(fieldName, value)}
            placeholder={definition.options?.placeholder}
            rows={definition.options?.rows}
          />
        );

      default:
        return (
          <TextField
            {...commonProps}
            testId={`uprof-input-${sanitizedKey}`}
            value={fieldValue || ""}
            onChange={(value) => setValue(fieldName, value)}
          />
        );
    }
  };

  // const onFormSubmit = (data: UserSettings) => { // This logic moves to the parent
  //   onSubmit(data);
  // };

  return (
    <>
      {Object.entries(settingsByCategory).map(
        ([category, categorySettings]) => (
          <CCard
            key={category}
            className="mb-4"
            data-testid={`uprof-card-${category}-settings`}
          >
            <CCardHeader>
              <h4
                className="mb-0"
                data-testid={`uprof-heading-${category}-settings`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)} Settings
              </h4>
            </CCardHeader>
            <CCardBody>
              <CRow>
                {categorySettings.map((definition) => (
                  <CCol key={definition.key} md="6" className="mb-3">
                    {renderField(definition)}
                  </CCol>
                ))}
              </CRow>
            </CCardBody>
          </CCard>
        ),
      )}
    </>
  );
};

export default DynamicSettingsForm;
